import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    CircularProgress,
    IconButton,
    FormControlLabel,
    Checkbox,
    Alert,
    Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VerifiedIcon from '@mui/icons-material/Verified';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import processNarrativeService from '../../services/ProcessNarrativeService';

/**
 * Dialog component for certifying process narratives
 * Allows users to provide certification notes and attestation
 */
const NarrativeCertificationDialog = ({ 
    open, 
    onClose, 
    appId, 
    appName, 
    versionId, 
    onCertificationComplete 
}) => {
    const [notes, setNotes] = useState('');
    const [attestation, setAttestation] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    
    const handleCertify = async () => {
        if (!attestation) {
            setError("You must attest to the accuracy of the narrative before certifying.");
            return;
        }
        
        try {
            setLoading(true);
            setError(null);
            
            const certificationData = {
                notes,
                attestation_date: new Date().toISOString()
            };
            
            if (versionId) {
                // Certify a specific version if version ID is provided
                await processNarrativeService.certifyNarrativeVersion(versionId, certificationData);
            } else {
                // Certify the current version if no version ID is provided
                if (!appId) {
                    setError("Application ID is required for certification");
                    setLoading(false);
                    return;
                }
                await processNarrativeService.certifyCurrentNarrative(appId, certificationData);
            }
            
            onCertificationComplete();
            onClose();
            
        } catch (error) {
            console.error("Error during certification:", error);
            setError("Failed to certify the narrative. Please try again.");
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <Dialog
            open={open}
            onClose={loading ? null : onClose}
            maxWidth="md"
            fullWidth
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <VerifiedIcon sx={{ mr: 1, color: 'primary.main' }} />
                    Certify Process Narrative
                </Box>
                <IconButton 
                    onClick={onClose} 
                    disabled={loading}
                    size="small"
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </DialogTitle>
            
            <DialogContent dividers>
                <Typography variant="subtitle1" gutterBottom>
                    You are certifying the process narrative for: <strong>{appName}</strong>
                </Typography>
                
                <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
                    <Typography variant="body2">
                        By certifying this process narrative, you attest that you have reviewed the documentation
                        and it accurately reflects the current access management controls for this application.
                        This certification will be recorded for compliance purposes.
                    </Typography>
                </Alert>
                
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom>
                        Certification Notes
                    </Typography>
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder="Enter any relevant notes about this certification (optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        disabled={loading}
                        variant="outlined"
                        size="small"
                    />
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Box sx={{ mt: 2 }}>
                    <FormControlLabel
                        control={
                            <Checkbox 
                                checked={attestation} 
                                onChange={(e) => setAttestation(e.target.checked)}
                                disabled={loading}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body2">
                                I attest that I have reviewed this process narrative and confirm it accurately 
                                reflects the current access management controls for this application.
                            </Typography>
                        }
                    />
                </Box>
                
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            
            <DialogActions>
                <Button 
                    onClick={onClose} 
                    disabled={loading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCertify}
                    disabled={loading || !attestation}
                    startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleOutlineIcon />}
                >
                    {loading ? 'Certifying...' : 'Certify Process Narrative'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default NarrativeCertificationDialog; 